import { FC } from 'react';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import { EditableDataTable, EditableColumn, ActionButton, Dropdown, DropdownItem } from '@/components/ui';
import { useCanEdit } from '@/hooks/useEditMode';
import { Target } from '../types';

interface TargetsTabProps {
  targets: Target[];
  onAdd: () => void;
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
}

/**
 * Sales Targets Tab Component
 * Manages LinkedIn prospect filters
 */
export const TargetsTab: FC<TargetsTabProps> = ({
  targets,
  onAdd,
  onEdit,
  onDelete,
}) => {
  const canEdit = useCanEdit();

  const columns: EditableColumn<Target>[] = [
    {
      key: 'name',
      header: 'Name',
      editable: true,
      validate: (value: string) => {
        if (!value?.trim()) return 'Name is required';
        return undefined;
      },
    },
    {
      key: 'role',
      header: 'Role',
      editable: true,
      validate: (value: string) => {
        if (!value?.trim()) return 'Role is required';
        return undefined;
      },
    },
    {
      key: 'industry',
      header: 'Industry',
      editable: true,
      validate: (value: string) => {
        if (!value?.trim()) return 'Industry is required';
        return undefined;
      },
    },
    {
      key: 'country',
      header: 'Country',
      editable: true,
    },
    {
      key: 'language',
      header: 'Language',
      editable: true,
    },
    ...(canEdit
      ? [
          {
            key: 'actions',
            header: 'Actions',
            editable: false,
            enableEditing: false,
            render: (_: any, index: number) => {
              const items: DropdownItem[] = [
                {
                  key: 'edit',
                  label: 'Edit',
                  icon: Edit2,
                  onClick: () => onEdit(index),
                  variant: 'info',
                },
                {
                  key: 'delete',
                  label: 'Delete',
                  icon: Trash2,
                  onClick: () => onDelete(index),
                  variant: 'danger',
                },
              ];

              return <Dropdown items={items} />;
            },
          },
        ]
      : []),
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            Sales Target Configuration
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage LinkedIn prospect filters
          </p>
        </div>
        {canEdit && (
          <ActionButton variant="success" onClick={onAdd}>
            <Plus className="w-4 h-4" />
            New Target
          </ActionButton>
        )}
      </div>

      {targets.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p>No targets configured yet.</p>
          {canEdit && (
            <p className="text-sm mt-2">Click "New Target" to create your first target.</p>
          )}
        </div>
      ) : (
        <EditableDataTable
          columns={columns}
          data={targets}
          keyExtractor={(target, index) => target.id ? `target-${target.id}` : `target-${index}`}
          enableEditing={canEdit}
          enableDelete={canEdit}
          initialEditingMode="cell"
        />
      )}
    </div>
  );
};

